import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { useSidebarStore } from '@/stores/sidebar-store';
import { OfflineIndicator, useBreakpoint, useSyncQueue } from '@kgc/ui';
import { memo, useCallback, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CommandPalette } from '../command';
import { UnifiedFAB } from '../unified-fab';
import { BottomNav } from './BottomNav';
import { AppBreadcrumb } from './Breadcrumb';
import { Header } from './Header';
import { WIZARD_ROUTES } from './nav-config';
import { NavDrawer } from './NavDrawer';
import { Sidebar } from './Sidebar';
import { SidebarOverlay } from './SidebarOverlay';

interface AppLayoutProps {
  children: ReactNode;
}

// Fix #9: Memoize heavy children so useSyncQueue re-renders only affect Header badge
const MemoizedSidebar = memo(Sidebar);

export function AppLayout({ children }: AppLayoutProps) {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { pendingCount } = useSyncQueue();
  const openPalette = useCallback(() => setCommandPaletteOpen(true), []);
  const { pathname } = useLocation();
  const { isMobile, isTablet } = useBreakpoint();
  const { openOverlay } = useSidebarStore();

  // Wizard módban: WizardShell kezeli scroll-t, sidebar és bottom nav elrejtve
  // F11 fix: query param és trailing slash eltávolítása az exact match előtt
  const cleanPathname = pathname.split('?')[0].replace(/\/$/, '');
  const isWizardRoute = (WIZARD_ROUTES as readonly string[]).includes(cleanPathname);

  // Swipe gesztúra — bal szélről jobbra → overlay/drawer nyílik
  useSwipeGesture({
    direction: 'right',
    onSwipe: openOverlay,
    disabled: isWizardRoute,
  });

  // Sidebar megjelenítési logika breakpoint szerint
  // Wizard módban: sidebar COLLAPSED (nem eltüntetve) — navigáció továbbra is elérhető
  const showSidebar = !isMobile && !isTablet; // lg+
  const showBottomNav = !isWizardRoute && isMobile;
  const showSidebarOverlay = !isWizardRoute; // tablet + mobil hamburger

  return (
    <div className="flex h-screen overflow-hidden kgc-bg">
      <OfflineIndicator position="top" />

      {/* Sidebar — desktop (lg+), wizard módban forced collapsed */}
      {showSidebar && <MemoizedSidebar forceCollapsed={isWizardRoute} />}

      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        <Header onCommandPaletteOpen={openPalette} pendingSyncCount={pendingCount} />
        {!isWizardRoute && <AppBreadcrumb />}
        <main
          className={
            isWizardRoute ? 'flex-1 overflow-hidden kgc-bg' : 'flex-1 overflow-y-auto kgc-bg pt-0'
          }
        >
          {children}
        </main>

        {/* Bottom Nav — csak mobil, nem wizard módban */}
        {showBottomNav && <BottomNav />}
      </div>

      {/* Overlay — tablet + mobil hamburger */}
      {showSidebarOverlay && <SidebarOverlay />}

      {/* NavDrawer — bottom sheet a BottomNav "Több" gombjára */}
      {!isWizardRoute && <NavDrawer />}

      <UnifiedFAB />
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
    </div>
  );
}
